import React, { useEffect } from 'react'

import {
  billingEngineQueries,
  useCoordinator,
  useCurrentUser,
  useProject,
} from '@builddocs/data-management'
import { useGetMyNotifications } from '@builddocs/data-management'
import {
  EmploymentButton,
  NotificationsButtonWithPopover,
} from '@builddocs/exdoc-app/feature-notifications'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import {
  AppBar,
  Stack,
  Toolbar,
  IconButton,
  Box,
  Theme,
  SwipeableDrawer,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useQuery } from 'react-query'
import { matchPath, useLocation } from 'react-router-dom'

import SideNavigation from '../side-navigation/SideNavigation'

import AddUserPosition from './add-user-position/AddUserPosition'
import AddUsersModal from './AddUsersModal'
import LogoButton from './atoms/LogoButton'
import ProcessUsers from './CreateOrganizationForUser'
import Navigation from './Navigation'
import { Notification } from './Notification'
import ProfileField from './ProfileField'
import { TariffsButton } from './TariffsButton'

const iOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent)

interface HeaderProps {
  isAccessError: boolean
}

export const Header = ({ isAccessError }: HeaderProps) => {
  const [isOpenDrawer, setIsOpenDrawer] = React.useState(false)
  const [isBannerExpanded, setIsBannerExpanded] = React.useState(false)
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))

  const location = useLocation()

  const [matchProjectsPage, matchOrganizations, matchProfile, matchTariffs] = [
    matchPath('/sakura', location.pathname),
    matchPath('/organization/*', location.pathname),
    matchPath('/my-account', location.pathname),
    matchPath('/tariffs/*', location.pathname),
  ]

  const user = useCurrentUser()
  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return
      }

      setIsOpenDrawer(open)
    }

  const { projectId } = useCoordinator()

  const { data: myNotifications } = useGetMyNotifications({ projectId })

  const { data: projectSettings } = useQuery(
    billingEngineQueries.getProjectSettings({ projectId }),
  )

  let showNotification = false
  let bannerHeight = 0

  const project = useProject(projectId)

  let content

  if (project?.project?.integrations?.includes('PIK')) {
    bannerHeight = isBannerExpanded ? 72 : 32
    content = (
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: '100%',
          }}
        >
          <div style={{ flex: 1 }}>
            {isBannerExpanded ? (
              <div>
                Уважаемые партнеры ГК ПИК! ООО «ПИК-УК» представляет Услуги по
                формированию исполнительной документации.
                <br />
                За более подробной информацией обращаться к руководителю группы
                ЦТУ Кузьминой И.А. +7 916 774 98 23.
              </div>
            ) : (
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Уважаемые партнеры ГК ПИК! ООО «ПИК-УК» представляет Услуги по
                формированию исполнительной документации.
              </div>
            )}
          </div>
          <IconButton
            size="small"
            sx={{
              color: 'white',
              padding: '4px',
            }}
            onClick={() => setIsBannerExpanded(!isBannerExpanded)}
          >
            {isBannerExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      </div>
    )

    showNotification = true
  }

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--main-content-height',
      `calc(100vh - ${
        104 + (myNotifications?.length ?? 0) * 56 + bannerHeight
      }px)`,
    )
    document.documentElement.style.setProperty(
      '--header-top-offset',
      `${56 + (myNotifications?.length ?? 0) * 56 + bannerHeight}px`,
    )
  }, [myNotifications?.length, bannerHeight])

  return (
    <React.Fragment>
      {!isDesktop &&
        !matchProjectsPage &&
        !matchProfile &&
        !matchOrganizations &&
        !isAccessError && (
          <SwipeableDrawer
            PaperProps={{
              sx: {
                width: 'calc(100% - 96px)',
                maxWidth: 512,
                minWidth: { xs: '100vw', sm: 196 },
              },
            }}
            anchor="left"
            disableBackdropTransition={!iOS}
            disableDiscovery={iOS}
            open={isOpenDrawer}
            onClose={toggleDrawer(false)}
            onOpen={toggleDrawer(true)}
          >
            <SideNavigation onClose={() => setIsOpenDrawer(false)} />
          </SwipeableDrawer>
        )}
      <AppBar
        elevation={0}
        position="relative"
        sx={{
          height: 56 + bannerHeight + (myNotifications?.length ?? 0) * 56,
          minHeight: 56 + bannerHeight + (myNotifications?.length ?? 0) * 56,
        }}
      >
        {showNotification && (
          <Box
            sx={{
              width: '100%',
              position: 'fixed',
              top: 0,
              height: `${bannerHeight}px`,
              minHeight: `${bannerHeight}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              zIndex: 1250,
              bgcolor: '#0034AD',
              paddingLeft: '16px',
              paddingRight: '16px',
              py: isBannerExpanded ? '8px' : '4px',
              textAlign: 'center',
            }}
          >
            {content}
          </Box>
        )}
        {myNotifications?.map((notification, index) => (
          <Notification
            key={notification.content}
            index={index}
            isLegacyShown={showNotification}
            legacyBannerHeight={bannerHeight}
            {...notification}
          />
        ))}

        <Toolbar
          sx={{
            px: '16px',
            position: 'fixed',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            top: bannerHeight + (myNotifications?.length ?? 0) * 56,
            minHeight: { xs: 56 },
            height: { xs: 56 },
            display: 'grid',
            gridTemplateColumns: {
              lg: '1fr auto 1fr',
              md: 'auto auto auto',
              xs: '1fr auto 1fr',
            },
            gridTemplateRows: '1fr auto',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            color: 'text.primary',
            zIndex: theme => theme.zIndex.appBar + 10,
            width: '100%',
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="flex-end"
            spacing={0.5}
            sx={{ width: 'fit-content' }}
          >
            {!isDesktop &&
            !matchProjectsPage &&
            !matchProfile &&
            !matchOrganizations ? (
              <IconButton sx={{ ml: 0.5 }} onClick={toggleDrawer(true)}>
                <MenuRoundedIcon sx={{ color: 'black' }} />
              </IconButton>
            ) : (
              <LogoButton />
            )}
            {(user.user.userId === 'decde69c-b16b-11ef-bf38-d00d126464f0' ||
              user.user.userId === 'b5a607cd-323b-11eb-bd3d-d00d126464f0') && (
              <AddUsersModal />
            )}
            {(user.user.userId === 'decde69c-b16b-11ef-bf38-d00d126464f0' ||
              user.user.userId === 'b5a607cd-323b-11eb-bd3d-d00d126464f0') && (
              <ProcessUsers />
            )}
            {(user.user.userId === 'decde69c-b16b-11ef-bf38-d00d126464f0' ||
              user.user.userId === 'b5a607cd-323b-11eb-bd3d-d00d126464f0') && (
              <AddUserPosition />
            )}
          </Stack>
          <Navigation />
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="flex-end"
            spacing={0.5}
          >
            {!!projectSettings?.shouldShowTariffMenu && (
              <TariffsButton isTarrifsPage={!!matchTariffs} />
            )}
            <NotificationsButtonWithPopover placement="bottom" size="large" />
            <EmploymentButton />
            <ProfileField />
          </Stack>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  )
}

export default Header
